import { BORDER_STYLES } from "../../constants";
import { createNumberInputGuards } from "../../helpers/numberInput";

const signedGuards = createNumberInputGuards({ allowNegative: true });
const unsignedGuards = createNumberInputGuards({ allowNegative: false });
const guardsFor = field => (field.allowNegative ? signedGuards : unsignedGuards);

const BLOCK_SELECTOR = "h1,h2,h3,h4,h5,h6,p,blockquote,pre,li";
const SIDE_ORDER = ["-top", "-right", "-bottom", "-left"];
const DEFAULT_BORDER_STYLE = "solid";
const DEFAULT_BORDER_WIDTH = "1";
const SPACING_TAB = "spacing";
const DECORATION_TAB = "decoration";
const PX_PATTERN = /^(-?\d*\.?\d+)px$/;
const RGB_PATTERN = /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d*\.?\d+))?\)$/;

const LINKED_ICON_NAME = "textStyleLinked";
const UNLINKED_ICON_NAME = "textStyleUnlinked";
const CHAIN_ARCS = '<path d="M10.6 8.2H8.6a3.8 3.8 0 0 0 0 7.6h2"/><path d="M13.4 8.2h2a3.8 3.8 0 0 1 0 7.6h-2"/>';
const CHAIN_SVG = attributes =>
  `<svg width="24" height="24" viewBox="0 0 24 24" focusable="false"><g fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round">${CHAIN_ARCS}${attributes}</g></svg>`;
const LINKED_ICON = CHAIN_SVG('<path d="M9 12h6"/>');
const UNLINKED_ICON = CHAIN_SVG('<path d="M9 12h1.6"/><path d="M13.4 12h1.6"/>');

const PADDING_FIELDS = [
  { name: "paddingTop", property: "padding-top", label: "Top", allowNegative: false },
  { name: "paddingBottom", property: "padding-bottom", label: "Bottom", allowNegative: false },
  { name: "paddingLeft", property: "padding-left", label: "Left", allowNegative: false },
  { name: "paddingRight", property: "padding-right", label: "Right", allowNegative: false },
];

const MARGIN_FIELDS = [
  { name: "marginTop", property: "margin-top", label: "Top", allowNegative: true },
  { name: "marginBottom", property: "margin-bottom", label: "Bottom", allowNegative: true },
  { name: "marginLeft", property: "margin-left", label: "Left", allowNegative: true },
  { name: "marginRight", property: "margin-right", label: "Right", allowNegative: true },
];

const BORDER_WIDTH_FIELD = { name: "borderWidth", property: "border-width", label: "Width (px)", allowNegative: false };

// `toggleName` is a button name, and TinyMCE builds body buttons as form fields, so it shows up as
// a key in getData(). Link state is therefore kept under `id` in its own object — merging getData()
// over it would silently unlink whichever group was not clicked.
const LINKED_GROUPS = [
  {
    id: "padding",
    shorthand: "padding",
    toggleName: "togglePaddingLink",
    label: "Padding (px)",
    fields: PADDING_FIELDS,
  },
  {
    id: "margin",
    shorthand: "margin",
    toggleName: "toggleMarginLink",
    label: "Margin (px, negative pulls closer)",
    fields: MARGIN_FIELDS,
  },
];

// The chain is a deliberate choice, so it outlives the dropdown: reopening the same block keeps
// the mode instead of deriving it again from values that may have drifted apart since.
const linkStates = new WeakMap();

const TOGGLE_NAMES = ["backgroundEnabled", "borderEnabled"];
const BORDER_FIELD_NAMES = [BORDER_WIDTH_FIELD.name, "borderStyle", "borderColor"];
const NUMBER_FIELDS = [...PADDING_FIELDS, ...MARGIN_FIELDS, BORDER_WIDTH_FIELD];
const STYLE_FIELD_NAMES = [
  ...NUMBER_FIELDS.map(field => field.name),
  ...BORDER_FIELD_NAMES,
  "backgroundColor",
  ...TOGGLE_NAMES,
];
// `border` is listed first so the reset also clears a shorthand that arrived with pasted content.
const BORDER_PROPERTIES = ["border", "border-width", "border-style", "border-color"];
const MANAGED_PROPERTIES = [
  ...LINKED_GROUPS.map(group => group.shorthand),
  ...NUMBER_FIELDS.map(field => field.property),
  "background-color",
  ...BORDER_PROPERTIES,
];

const getTargetBlocks = editor => {
  const selected = editor.selection.getSelectedBlocks().filter(block => editor.dom.is(block, BLOCK_SELECTOR));

  if (selected.length) {
    return selected;
  }

  const parent = editor.dom.getParent(editor.selection.getNode(), BLOCK_SELECTOR);

  return parent ? [parent] : [];
};

// The CSSOM serialises every colour it stores as rgb(), which colorinput shows as a raw string
// instead of a swatch, so it is mapped back to the hex the picker produced. A translucent colour
// has no hex the picker understands, so it is left as it is rather than flattened to opaque.
const toHexColor = value => {
  const parsed = RGB_PATTERN.exec(value || "");

  if (!parsed || (parsed[4] !== undefined && Number(parsed[4]) < 1)) {
    return value || "";
  }

  return `#${parsed
    .slice(1, 4)
    .map(channel => Number(channel).toString(16).padStart(2, "0"))
    .join("")}`;
};

const readLength = (block, property) => {
  const parsed = PX_PATTERN.exec(block.style.getPropertyValue(property));

  return parsed ? parsed[1] : "";
};

const readCurrent = block => {
  const backgroundColor = toHexColor(block.style.getPropertyValue("background-color"));
  const borderStyle = block.style.getPropertyValue("border-style");
  const state = {
    backgroundEnabled: backgroundColor !== "",
    backgroundColor,
    borderEnabled: borderStyle !== "" && borderStyle !== "none",
    borderStyle: borderStyle && borderStyle !== "none" ? borderStyle : DEFAULT_BORDER_STYLE,
    borderColor: toHexColor(block.style.getPropertyValue("border-color")),
  };

  NUMBER_FIELDS.forEach(field => {
    state[field.name] = readLength(block, field.property);
  });

  // A border with no width falls back to the browser's `medium` (3px), which reads as a bug
  // beside an empty width field.
  if (state.borderWidth === "") {
    state.borderWidth = DEFAULT_BORDER_WIDTH;
  }

  return state;
};

// Sides that already differ open unlinked, so reopening the dialog cannot flatten them.
const readLinkState = state =>
  LINKED_GROUPS.reduce((linked, group) => {
    linked[group.id] = group.fields.every(field => state[field.name] === state[group.fields[0].name]);

    return linked;
  }, {});

const setStyle = (block, property, value, priority = "") => {
  if (value === "") {
    block.style.removeProperty(property);
  } else {
    block.style.setProperty(property, value, priority);
  }
};

const toLength = (field, value) => {
  const number = guardsFor(field).toCssNumber(value);

  return number === "" ? "" : `${number}px`;
};

const setLength = (block, field, value) => {
  setStyle(block, field.property, toLength(field, value));
};

// TinyMCE's serializer compresses four longhands into one shorthand and inlines every
// `!important` inside it - `padding: 10px !important 20px !important ...` is invalid CSS, so the
// browser drops all four sides on the next parse. Writing the shorthand here round-trips intact.
const setSpacing = (block, group, state) => {
  const ordered = SIDE_ORDER.map(suffix => group.fields.find(field => field.property.endsWith(suffix)));
  const lengths = ordered.map(field => toLength(field, state[field.name]));

  block.style.removeProperty(group.shorthand);
  ordered.forEach(field => block.style.removeProperty(field.property));

  // Theme CSS wins over plain inline styles here: post.scss and editor.scss both set paragraph
  // and heading spacing with !important, so an inline override has to carry it too. Background
  // and border have no such competition, so they stay overridable.
  if (lengths.every(length => length !== "")) {
    block.style.setProperty(group.shorthand, lengths.join(" "), "important");

    return;
  }

  ordered.forEach((field, index) => setStyle(block, field.property, lengths[index], "important"));
};

// TinyMCE serialises `data-mce-style` in preference to the live style attribute and only keeps
// the two in sync for writes made through its own dom API. Styles written straight to the CSSOM
// show up in the editor but are replaced by the stale shadow copy on save, so it is updated here.
const commitStyle = block => {
  if (!block.style.length) {
    block.removeAttribute("style");
    block.removeAttribute("data-mce-style");

    return;
  }

  block.setAttribute("data-mce-style", block.getAttribute("style"));
};

// A linked group shows one input for four sides, so editing it commits all four.
const spreadLinkedSides = (state, linked, touched) => {
  LINKED_GROUPS.filter(group => linked[group.id]).forEach(group => {
    const edited = group.fields.some(field => touched.has(field.name));

    group.fields.forEach(field => {
      state[field.name] = state[group.fields[0].name];

      if (edited) {
        touched.add(field.name);
      }
    });
  });
};

// The dialog reads its values from the first block, so writing everything would push that
// block's background and borders onto the rest of a multi-block selection, and would drop any
// length this dialog cannot parse. Only the properties the user edited are written.
const applyTextStyle = (block, state, touched) => {
  const editedGroups = LINKED_GROUPS.filter(group => group.fields.some(field => touched.has(field.name)));

  editedGroups.forEach(group => setSpacing(block, group, state));

  if (touched.has("backgroundEnabled") || touched.has("backgroundColor")) {
    setStyle(block, "background-color", state.backgroundEnabled ? (state.backgroundColor || "").trim() : "");
  }

  if (touched.has("borderEnabled") || BORDER_FIELD_NAMES.some(name => touched.has(name))) {
    if (state.borderEnabled) {
      setStyle(block, "border-style", state.borderStyle);
      setLength(block, BORDER_WIDTH_FIELD, state.borderWidth);
      setStyle(block, "border-color", (state.borderColor || "").trim());
    } else {
      BORDER_PROPERTIES.forEach(property => block.style.removeProperty(property));
    }
  }

  commitStyle(block);
};

const clearTextStyle = block => {
  MANAGED_PROPERTIES.forEach(property => block.style.removeProperty(property));
  commitStyle(block);
};

const registerTextStyleIcons = editor => {
  editor.ui.registry.addIcon(LINKED_ICON_NAME, LINKED_ICON);
  editor.ui.registry.addIcon(UNLINKED_ICON_NAME, UNLINKED_ICON);
};

const openTextStyleDialog = editor => {
  const blocks = getTargetBlocks(editor);

  if (!blocks.length) {
    editor.notificationManager.open({
      text: "Place the cursor inside a heading, paragraph or list item first.",
      type: "warning",
      timeout: 3000,
    });

    return;
  }

  const state = readCurrent(blocks[0]);
  const originalStyles = blocks.map(block => block.getAttribute("style"));
  const touched = new Set();
  let dirty = false;
  let committed = false;
  let activeTab = SPACING_TAB;
  const linked = linkStates.get(blocks[0]) || readLinkState(state);

  linkStates.set(blocks[0], linked);
  const visibleFields = group => (linked[group.id] ? [group.fields[0]] : group.fields);

  // Every edit lands in the editor as it is typed, so nothing here goes through undoManager:
  // one level is added when the dialog closes, instead of one per keystroke.
  const applyToBlocks = () => {
    spreadLinkedSides(state, linked, touched);

    if (!touched.size) {
      return;
    }

    dirty = true;
    blocks.forEach(function (block) {
      applyTextStyle(block, state, touched);
    });
  };

  const restoreOriginalStyles = () => {
    blocks.forEach((block, index) => {
      if (originalStyles[index] === null) {
        block.removeAttribute("style");
      } else {
        block.setAttribute("style", originalStyles[index]);
      }

      commitStyle(block);
    });
  };

  const syncState = data => {
    STYLE_FIELD_NAMES.filter(name => name in data).forEach(name => {
      if (data[name] !== state[name]) {
        touched.add(name);
      }

      state[name] = data[name];
    });
  };

  // Linked, the group label above already says which sides the single input covers.
  const toSpacingField = (group, field) =>
    linked[group.id] ? { type: "input", name: field.name } : { type: "input", name: field.name, label: field.label };

  const toGroupLabel = group => ({ type: "htmlpanel", html: `<label class="tox-label">${group.label}</label>` });

  const toToggle = group => ({
    type: "button",
    name: group.toggleName,
    text: "",
    icon: linked[group.id] ? LINKED_ICON_NAME : UNLINKED_ICON_NAME,
    borderless: true,
  });

  // `panel` is the only body component that takes a class, so every row is one and shortcodes.scss lays it out.
  const toSpacingSection = group => ({
    type: "panel",
    classes: ["text-style-section"],
    items: [
      {
        type: "panel",
        classes: ["text-style-section__heading"],
        items: linked[group.id] ? [toGroupLabel(group)] : [toGroupLabel(group), toToggle(group)],
      },
      linked[group.id]
        ? {
            type: "panel",
            classes: ["text-style-section__row"],
            items: [
              toSpacingField(group, group.fields[0]),
              { type: "panel", classes: ["text-style-section__toggle"], items: [toToggle(group)] },
            ],
          }
        : {
            type: "grid",
            columns: 2,
            items: group.fields.map(field => toSpacingField(group, field)),
          },
    ],
  });

  // redial validates initialData against the components it is given, so hidden sides stay in
  // `state` only and come back the moment the group is unlinked again.
  const toInitialData = () => {
    const names = [...TOGGLE_NAMES];

    if (state.backgroundEnabled) {
      names.push("backgroundColor");
    }

    if (state.borderEnabled) {
      names.push(...BORDER_FIELD_NAMES);
    }

    LINKED_GROUPS.forEach(group => {
      visibleFields(group).forEach(field => names.push(field.name));
    });

    return names.reduce((data, name) => {
      data[name] = state[name];

      return data;
    }, {});
  };

  // The rebuilt tabpanel selects its first tab on attach, which fires onTabChange and overwrites
  // `activeTab` before it can be restored - hence the copy taken up front.
  const rebuild = api => {
    const tab = activeTab;

    api.redial(buildSpec());
    api.showTab(tab);
  };

  const buildSpec = () => ({
    title: "Text style",
    size: "medium",
    body: {
      type: "tabpanel",
      tabs: [
        {
          name: SPACING_TAB,
          title: "Spacing",
          items: LINKED_GROUPS.map(toSpacingSection),
        },
        {
          name: DECORATION_TAB,
          title: "Background & border",
          items: [
            { type: "checkbox", name: "backgroundEnabled", label: "Background color" },
            ...(state.backgroundEnabled ? [{ type: "colorinput", name: "backgroundColor", label: "Color" }] : []),
            { type: "checkbox", name: "borderEnabled", label: "Border" },
            ...(state.borderEnabled
              ? [
                  {
                    type: "grid",
                    columns: 3,
                    items: [
                      { type: "input", name: BORDER_WIDTH_FIELD.name, label: BORDER_WIDTH_FIELD.label },
                      { type: "listbox", name: "borderStyle", label: "Style", items: BORDER_STYLES },
                      { type: "colorinput", name: "borderColor", label: "Color" },
                    ],
                  },
                ]
              : []),
          ],
        },
      ],
    },
    initialData: toInitialData(),
    buttons: [
      {
        type: "custom",
        name: "removeButton",
        text: "Remove style",
        align: "start",
      },
      {
        type: "cancel",
        name: "closeButton",
        text: "Cancel",
      },
      {
        type: "submit",
        name: "submitButton",
        text: "Apply",
        primary: true,
      },
    ],
    onChange: function (api) {
      const data = api.getData();
      const corrected = {};

      NUMBER_FIELDS.filter(field => field.name in data).forEach(field => {
        const sanitised = guardsFor(field).sanitise(data[field.name]);

        if (sanitised !== data[field.name]) {
          corrected[field.name] = sanitised;
        }
      });

      const toggled = TOGGLE_NAMES.some(name => name in data && data[name] !== state[name]);

      syncState(data);
      syncState(corrected);

      if (Object.keys(corrected).length) {
        api.setData(corrected);
      }

      applyToBlocks();

      if (toggled) {
        rebuild(api);
      }
    },
    onAction: function (api, details) {
      if (details.name === "removeButton") {
        blocks.forEach(clearTextStyle);
        dirty = true;
        committed = true;
        api.close();

        return;
      }

      const group = LINKED_GROUPS.find(candidate => candidate.toggleName === details.name);

      if (!group) {
        return;
      }

      syncState(api.getData());
      // Unlinking has to reveal four sides holding the value the single field was showing, not
      // whatever they held before they were tethered.
      spreadLinkedSides(state, linked, touched);
      linked[group.id] = !linked[group.id];
      applyToBlocks();

      rebuild(api);
    },
    onSubmit: function (api) {
      committed = true;
      api.close();
    },
    onTabChange: function (api, details) {
      activeTab = details.newTabName;
    },
    onClose: function () {
      if (!dirty) {
        return;
      }

      if (!committed) {
        restoreOriginalStyles();

        return;
      }

      editor.undoManager.add();
      editor.fire("change");
    },
  });

  // Anchored to the toolbar rather than centred over the page: no backdrop, and the text
  // being styled stays visible while the sides are typed in.
  editor.windowManager.open(buildSpec(), { inline: "toolbar" });
};

export { openTextStyleDialog, registerTextStyleIcons };
